import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import Button from "designSystem/components/button/Button";
import { POPOVER_BOTTOM_END } from "designSystem/styles/popover-positions";
import Grid, { GridProps } from "designSystem/components/grid/Grid";
import { CsiImage, IconButton, Placeholder, Popover, SingleSelect } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import Text from "designSystem/components/text/Text";
import csiPrefs from "csi/prefs/_base";
import { observer } from "mobx-react-lite";
import React, { Fragment, FunctionComponent, MouseEventHandler, useState } from "react";
import AttributeSettingStore, { AttributeSettingStoreContext } from "../AttributeSettingStore";
import NoAttributesImage from "./NoAttributesImage.png";
import { useRuleStore } from "./RuleStore";
import SingleRuleGrid from "./SingleRuleGrid";

const PREFIX = "RulePane";

const classes = {
	paper: `${PREFIX}paper`,
};

const StyledGrid = styled(Grid)(({
	theme,
}) => ({
	[`& .${classes.paper}`]: {
		marginTop: theme.spacing(3),
	},
}));

interface RulePaneProps extends GridProps {
	/** component for attribute settings */
	SettingComponent?: FunctionComponent;

	/** store for setting component */
	settingComponentStore?: AttributeSettingStore;
}

const RulePane: FunctionComponent<RulePaneProps> = observer((props) => {
	const ruleStore = useRuleStore();

	const loc = useLocale();
	const { SettingComponent, settingComponentStore, ...otherProps } = props;
	const { closePopup } = settingComponentStore || {};

	const [anchorEl, setAnchorEl] = useState(null);

	/** open up the pop up */
	const handleClick: MouseEventHandler = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const {
		addRule,
		allConditions,
		count,
		ruleLimit,
		selectedCondition,
		setSelectedCondition,
		indexes,
		options,
	} = ruleStore || {};

	const open = !!anchorEl;

	/** save the list when close the popup */
	const handleClose = () => {
		setAnchorEl(null);
		closePopup();
	};

	// do the localization
	if (allConditions?.length > 0) {
		for (const condition of allConditions) {
			condition.name = loc.resource(condition.id);
		}
	}

	if (selectedCondition) {
		selectedCondition.name = loc.resource(selectedCondition.id);
	}

	return (
		<StyledGrid
			container
			spacing={3}
			{...otherProps}
		>
			<Grid
				container
				item
				justifyContent="space-between"
			>
				<Grid item>
					<Text
						block
						bold
						res="RuleCreate"
					/>
				</Grid>
				<Grid item>
					{(options?.length > 0) && csiPrefs.hasPersonalAccess() && (
						<IconButton
							design="outline"
							onClick={handleClick}
							res="RulePaneSettingIconTooltip"
							size="large"
						>
							<SettingsIcon />
						</IconButton>
					)}
					<Popover
						{...POPOVER_BOTTOM_END}
						anchorEl={anchorEl}
						className={classes.paper}
						onClose={handleClose}
						open={open}
					>
						<AttributeSettingStoreContext.Provider value={settingComponentStore}>
							<SettingComponent />
						</AttributeSettingStoreContext.Provider>
					</Popover>
				</Grid>
			</Grid>
			{(options?.length == 0) && (
				<Grid item>
					<Placeholder
						subtitleMargin={6}
						subtitleProps={{ res: "PPNoAttributesAvailable_SubTitle" }}
						titleProps={{ res: "PPNoAttributesAvailable_Title" }}
					>
						{{
							image:
								<CsiImage src={NoAttributesImage} />
							,
						}}
					</Placeholder>
				</Grid>
			)}
			{(options?.length > 0) && (
				<Fragment>
					<Grid
						alignItems="center"
						container
						item
						spacing={2}
					>
						<Grid item>
							<Text res="RuleGuideline1" />
						</Grid>
						{allConditions && (
							<Grid item>
								<SingleSelect
									inline
									onChange={(event, newValueItem, reason) => setSelectedCondition(newValueItem)}
									options={allConditions}
									required
									value={selectedCondition}
								/>
							</Grid>
						)}
						<Grid item>
							<Text res="RuleGuideline2" />
						</Grid>
					</Grid>
					<Grid
						alignItems="center"
						container
						item
						spacing={2}
					>
						{ruleStore &&
							indexes.map((index) => (
								<SingleRuleGrid
									index={index}
									key={`singleRule-${index}`}
								/>
							))
						}
					</Grid>
					<Grid container item>
						<Button
							design="outline"
							disabled={count >= ruleLimit}
							onClick={() => addRule()}
							res="RuleAddButton"
							startIcon={<AddIcon />}
						/>
					</Grid>
				</Fragment>
			)}
		</StyledGrid>
	);
});

export default RulePane;